import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  IconButton,
  Box,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import { MoreVert, ContentCopy } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const SessionsTable = ({
  sessions,
  sortBy,
  sortOrder,
  onSort,
  onCopyUserId,
  onMessageUser,
  onViewUser,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  const handleMenuOpen = (event, session) => {
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSession(null);
  };

  const handleCopyUserId = () => {
    if (selectedSession) {
      onCopyUserId(selectedSession.userId);
    }
    handleMenuClose();
  };

  const handleMessageUser = () => {
    if (selectedSession) {
      onMessageUser(selectedSession.userId);
    }
    handleMenuClose();
  };

  const handleViewUser = () => {
    if (selectedSession && onViewUser) {
      onViewUser(selectedSession.userId);
    }
    handleMenuClose();
  };

  const getTimeAgo = (visitedAt) => {
    if (!visitedAt) return { label: "Unknown", color: "default" };

    const now = dayjs();
    const visitTime = dayjs(visitedAt);
    const hoursSince = now.diff(visitTime, "hour");
    const daysSince = now.diff(visitTime, "day");

    if (hoursSince < 1) {
      return { label: "Just now", color: "success" };
    } else if (hoursSince < 24) {
      return {
        label: `${hoursSince}h ago`,
        color: hoursSince <= 6 ? "success" : "warning",
      };
    } else if (daysSince <= 7) {
      return {
        label: `${daysSince}d ago`,
        color: daysSince <= 3 ? "warning" : "default",
      };
    } else {
      return { label: `${daysSince}d ago`, color: "error" };
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? sortOrder : "asc"}
                onClick={() => onSort("name")}
              >
                User
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "visitedAt"}
                direction={sortBy === "visitedAt" ? sortOrder : "asc"}
                onClick={() => onSort("visitedAt")}
              >
                Visited At
              </TableSortLabel>
            </TableCell>
            <TableCell>Time Ago</TableCell>
            <TableCell>User ID</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((session, index) => {
            const timeAgo = getTimeAgo(session.visitedAt);

            return (
              <TableRow
                key={`${session.userId}-${session.visitedAt}-${index}`}
                hover
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {session.name || "Anonymous"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {session.email || "No email"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {session.visitedAt
                        ? dayjs(session.visitedAt).format("MMM DD, YYYY")
                        : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {session.visitedAt
                        ? dayjs(session.visitedAt).format("HH:mm:ss")
                        : ""}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={timeAgo.label}
                    size="small"
                    color={timeAgo.color}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {session.userId?.slice(0, 12)}...
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => onCopyUserId(session.userId)}
                      sx={{ padding: 0.5 }}
                    >
                      <ContentCopy sx={{ fontSize: "0.875rem" }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, session)}
                  >
                    <MoreVert sx={{ fontSize: "1.25rem" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {sessions.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No sessions found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCopyUserId}>Copy User ID</MenuItem>
        <MenuItem onClick={handleMessageUser}>Message User</MenuItem>
        <MenuItem onClick={handleViewUser}>View user</MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default SessionsTable;
